import 'components/sidebar/index.css'
import './index.css'
import './login-loader.css'

import React, { useCallback, useEffect } from 'react'
import { useMediaQuery } from 'react-responsive'
import { useLocation } from 'react-router-dom'

import api from '@disco/api/server'
import {
  autologinUser,
  loginUser,
} from '@disco/state-management/actions/UserActions'
import {
  getRequestsCompleted,
  getRequestsTotal,
} from '@disco/state-management/selectors/app'
import { isShortenedDomainUrl } from '@disco/utils/hostnames'
import { isLoggingInFromAccountsSubdomain } from '@disco/api/utils/auth'

import appearsOnFilterImage from 'assets/images/appears-on-filter.png'

import { useRegionCookieConsentContext } from 'contexts/cookie-consent'

import BrowserModal from 'components/browser-modal'
import LoginForm from 'components/common/login-form'
import CookiesDisabled from 'components/cookies-disabled'
import DocumentHeadTags from 'components/document-head-tags'
import { DiscoStackLoader, ProgressBarLoader } from 'components/ds'
import CookieBanner from 'components/modals/cookie-banner'
import Heading from 'components/ui/heading'
import Logo from 'components/ui/logo'

import ShortlinkNotFound from 'pages/shortlink-not-found'

import { useDispatch, useSelector } from 'hooks/use-redux'
import { useTranslation } from '@disco/i18n/use-translation'

const Login = (): React.ReactElement<'div'> | React.ReactNode => {
  const { t } = useTranslation('accounts')
  const routerLocation = useLocation()

  // Parse query parameters from URLSearchParams
  const searchParams = new URLSearchParams(routerLocation.search)
  const query = {
    return: searchParams.get('return') || undefined,
    next: searchParams.get('next') || undefined,
    sessionExpired: searchParams.get('sessionExpired') || undefined,
    reason: searchParams.get('reason') || undefined,
  }

  // Create compatible location object for legacy components
  const location = {
    ...routerLocation,
    query,
  }
  const user = useSelector(state => state.user)
  const numCompleted = useSelector(getRequestsCompleted)
  const numTotal = useSelector(getRequestsTotal)
  const dispatch = useDispatch()
  const cookieConsentContext = useRegionCookieConsentContext()
  const isDesktop = useMediaQuery({
    query: '(min-width: 701px)',
  })

  const handleForgotPassword = useCallback(
    (email: string, returnUrl: string) => {
      return api.sendPasswordRecoveryInstructions(email, returnUrl)
    },
    [],
  )

  useEffect(() => {
    dispatch(autologinUser(query.return || query.next))
  }, [])

  if (isShortenedDomainUrl()) {
    return <ShortlinkNotFound />
  }

  if (user.autologgingIn || user.loggedIn) {
    const progressValue = (Math.min(numCompleted, numTotal) / numTotal) * 100

    return (
      <>
        <ProgressBarLoader value={progressValue} />
        <div className="LoginPage_LoaderCont">
          <div className="LoginPage_Loader">
            <DiscoStackLoader />
          </div>
        </div>
      </>
    )
  }

  return (
    <div className="LoginPage">
      <div className="LoginPage_Title">
        {/* TODO: extract Sidebar_Logo into logo component */}
        <div className="Sidebar_Head">
          <Logo className="Sidebar_Logo" />
        </div>
      </div>
      <div className="LoginPage_Container">
        <DocumentHeadTags title={t('accounts:loginDisco', 'Login | DISCO')} />
        <CookiesDisabled />
        <BrowserModal />
        <CookieBanner location="Login" />

        <div className="LoginPage_FormContainer">
          <LoginForm
            user={user}
            location={location}
            onLoginUser={(loginDetails, returnPath) => {
              return dispatch(
                loginUser(loginDetails, returnPath, null, {
                  cookieConsentContext,
                  location: isLoggingInFromAccountsSubdomain()
                    ? 'Accounts'
                    : 'Login',
                }),
              )
            }}
            onForgotPassword={handleForgotPassword}
            onGetSignupUrl={() =>
              Promise.resolve('https://accounts.disco.ac/signup')
            }
            onGetBusiness={async () => {
              return await api.getCurrentBusinessPublic()
            }}
          />
        </div>

        {isDesktop && (
          <div className="LoginPage_NewsCont LoginPage_NewsBg">
            <div className="LoginPage_NewsBlock">
              <div className="LoginPage_HeadingCont">
                {t('accounts:whatsNew', 'What’s new')}
              </div>
              <Heading size={700}>
                {t(
                  'accounts:whatsNewAppearsOnTitle',
                  'Find tracks without playlists',
                )}
              </Heading>
              <img
                className="LoginPage_NewsImage"
                src={appearsOnFilterImage}
                alt={t(
                  'accounts:whatsNewAppearsOnImageAlt',
                  'The “Appears on” filter on the search page',
                )}
              />
              <div className="LoginPage_NewsBody">
                <p>
                  {t(
                    'accounts:whatsNewAppearsOnDescription1',
                    'Ever wondered how many tracks you have that aren’t in any playlists and possibly were never shared before? Now you can find these instantly.',
                  )}
                </p>
                <p>
                  {t(
                    'accounts:whatsNewAppearsOnDescription2',
                    'On the search page, look for the new “Appears on” filter.',
                  )}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default Login
